import './OperationsGuide.css';

import { GUIDE_SECTIONS } from './operationsGuideSections';
import { Link } from 'react-router-dom';
import React from 'react';
import { useLocation } from 'react-router-dom';

const P1 = <span className="guide-pill guide-pill-p1">P1</span>;
const P2 = <span className="guide-pill guide-pill-p2">P2</span>;
const P3 = <span className="guide-pill guide-pill-p3">P3</span>;

/**
 * The operations guide, rendered in the app rather than kept as a document.
 *
 * Every working page links in here by anchor, so a question asked in front of a
 * control is answered without leaving the tool. Section ids live in
 * operationsGuideSections.ts so those links are type-checked.
 */
const OperationsGuide: React.FC = () => {
  const location = useLocation();
  const hash = location.hash.replace('#', '');

  // Routed navigation does not move the viewport on its own, so an incoming
  // /guide#anchor has to be scrolled to here.
  React.useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const target = document.getElementById(hash);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  const jump = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="guide-page">
      <header className="guide-header">
        <h1>Operations Guide</h1>
        <p className="guide-lede">
          How to work the Open Scheduling backlog with this tool. Written for the people doing the
          work, not for the people who built it.
        </p>
      </header>

      <div className="guide-layout">
        <nav className="guide-toc" aria-label="Guide contents">
          <h2>Contents</h2>
          <ol>
            {GUIDE_SECTIONS.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  className={hash === section.id ? 'guide-toc-active' : ''}
                  onClick={() => jump(section.id)}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div>
          <section className="guide-section" id="start-here">
            <h2>Start here</h2>
            <div className="guide-callout guide-callout-key">
              <p>
                If you read nothing else: start on the <strong>Dashboard</strong>, take your work
                from the <strong>Department Work Queue</strong> first, and only then go provider by
                provider in the <strong>Provider Work Queue</strong>.
              </p>
            </div>
            <p>
              One Epic change in a department can unblock dozens of providers at once. The same
              change worked one provider at a time is dozens of tickets. That is the whole reason
              the department queue exists, and it is why it comes before the provider list.
            </p>
            <p>
              Do not work the provider list top to bottom without filtering. It is thousands of rows
              and it is sorted by nothing that matters until you tell it what matters.
            </p>
          </section>

          <section className="guide-section" id="what-it-measures">
            <h2>What the tool measures</h2>

            <h3 id="measures-cadence">Where the numbers come from</h3>
            <p>
              Every figure comes from an <strong>Epic Open Scheduling diagnostic import</strong> - a
              point-in-time extract, not a live call. Two cadences feed it:
            </p>
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Cadence</th>
                  <th>What it carries</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Weekly</td>
                  <td>
                    The full Epic configuration workbook: every provider, and every provider x
                    department x visit-type pairing.
                  </td>
                </tr>
                <tr>
                  <td>Nightly</td>
                  <td>A refresh of scheduling rules for a subset of providers.</td>
                </tr>
              </tbody>
            </table>
            <p>
              The Dashboard header tells you when each last ran. If the nightly refresh is not
              running in your environment, the page says so, and every figure comes from the weekly
              workbook alone.
            </p>
            <p>
              A <span className="guide-term">pairing</span> (also called a combo) is one provider, in
              one department, for one visit type. That is the unit Epic evaluates, and one provider
              usually has many of them.
            </p>

            <h3 id="measures-published">Published vs not published</h3>
            <p>
              A department is <span className="guide-term">published</span> when it appears on a
              Symplr Marketing Master location, meaning a patient can actually find it.
            </p>
            <div className="guide-callout guide-callout-key">
              <p>
                <strong>A fix on an unpublished department changes nothing a patient sees.</strong>{' '}
                Nearly every ranking in this tool applies the publication filter. Without it, the top
                of the list is operating rooms, cath labs, nuclear medicine and echo - they have the
                most providers behind a single flag, and none of them are meant to be
                open-schedulable.
              </p>
            </div>

            <h3 id="measures-roster">The Open Scheduling roster</h3>
            <p>
              Coverage is measured against the <strong>Open Scheduling roster</strong>: providers
              marked active <em>and</em> flagged for online scheduling in the provider directory. The
              directory decides who <em>should</em> be bookable; the Epic import only describes how
              they are <em>set up</em>. Providers who are not on the roster are excluded from the
              headline figures on purpose - fixing Epic for them would change nothing a patient sees.
            </p>
          </section>

          <section className="guide-section" id="daily-routine">
            <h2>The daily routine</h2>
            <ol>
              <li>
                <strong>Dashboard.</strong> Read the top three sections - coverage, patient impact,
                momentum. That is the state of the world in about a minute.
              </li>
              <li>
                <strong>Fix once, unblock many</strong> at the bottom of the Dashboard, then open the{' '}
                <Link to="/departments">Department Work Queue</Link> and clear the top rows.
              </li>
              <li>
                <strong>Provider Work Queue, filtered to {P1}</strong> - the individual blackouts no
                department item covers.
              </li>
              <li>
                <strong>&ldquo;Looks fixed - confirm&rdquo; rows.</strong> Close out work the data
                already proves is done. This is the cheapest backlog reduction available.
              </li>
              <li>Everything else.</li>
            </ol>
          </section>

          <section className="guide-section" id="dashboard">
            <h2>
              Dashboard <Link to="/dashboard" className="guide-hint-block guide-hint">Open the page</Link>
            </h2>

            <h3 id="dashboard-coverage">Patient-visible coverage</h3>
            <p>
              The headline percentage: providers on the roster for whom Epic returns a bookable slot.
              The pill beside it compares against the previous import.
            </p>
            <p>
              The smaller figure beside it is <strong>bookable on the public site</strong>: of those,
              the providers with a slot at a department on one of their own published Marketing
              Master locations, which is what Find Care can actually show. The difference is
              publishing work in Symplr, not Epic work. The call-out under the chart splits it into
              slots at departments that are not on the provider&apos;s published locations, and
              providers with no published location at all, and links to the{' '}
              <strong>Publishing</strong> view of the provider queue.
            </p>
            <p>Two warnings can appear here:</p>
            <ul>
              <li>
                <strong>&ldquo;not described by the latest configuration import at all&rdquo;</strong>{' '}
                - the import missed them. Nothing is known about their Epic setup, so they count as
                not visible. This is an import problem, not an Epic problem.
              </li>
              <li>
                <strong>&ldquo;predates roster reconciliation&rdquo;</strong> - the figures use
                today&apos;s directory rather than the directory as it stood when the import ran.
                Treat small movements with caution.
              </li>
            </ul>

            <h3 id="dashboard-impact">Patient impact today</h3>
            <p>
              Three clickable cards. Each deep-links into the Provider Work Queue pre-filtered to that
              tier.
            </p>
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Card</th>
                  <th>Meaning</th>
                  <th>Urgency</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="guide-wrap">{P1} Invisible to patients</td>
                  <td>
                    Active, blocked on a department patients can reach, and <strong>nothing
                    bookable anywhere</strong>. A patient searching that location finds nothing.
                  </td>
                  <td>Work these first.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">{P2} Partially visible</td>
                  <td>
                    Active, blocked on a reachable department, but bookable somewhere else. Part of
                    their availability is missing from the public site.
                  </td>
                  <td>Second.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">{P3} Not yet patient-facing</td>
                  <td>
                    The Epic fix is still needed, but none of the blocked departments are published
                    yet.
                  </td>
                  <td>Real work, no patient impact today. Do not let it crowd out {P1}.</td>
                </tr>
              </tbody>
            </table>
            <p>
              The footnote below the cards counts providers who appear in the Epic data but are not
              on the roster. They are excluded from every figure above deliberately.
            </p>

            <h3 id="dashboard-resolution">Progress toward resolution</h3>
            <p>
              Starts from the coverage gap: every roster provider who is not visible to patients,
              so its total is the headline&rsquo;s roster count minus its visible count. A bridge row
              splits that gap into P1, P3, fully booked with nothing blocked, and maintenance only,
              then subtracts providers excluded by a note and providers covered by a department
              item. What is left is the provider work items, shown as a funnel by the work-queue
              status people record by hand. P2 providers are not in this panel: they have slots
              somewhere, so the headline already counts them as visible. Two call-outs matter:
            </p>
            <ul>
              <li>
                <strong>No note against them at all</strong> - nobody has looked at them yet.
              </li>
              <li>
                <strong>Covered by a department item</strong> - deducted from the open count and
                tracked once in the department queue instead.
              </li>
            </ul>
            <div className="guide-callout">
              <p>
                Providers whose only findings are no active department or inactive departments stay
                available in the maintenance/onboarding view, but do not count as an online-scheduling
                opportunity until they become eligible.
              </p>
              <p>
                Covered items are deducted from <em>resolution</em> but still counted under{' '}
                <em>patient impact</em>. That is intentional: impact measures what a patient sees,
                resolution measures work outstanding. The two totals are meant to differ.
              </p>
            </div>
            <p>
              Work-queue notes can assign an item to Records, Scheduling, or Enterprise. They can
              also mark an intentional configuration or a temporary exclusion. Active exclusions
              stay in the audit history but do not contribute to the unresolved count; temporary
              exclusions return automatically on their review date.
            </p>

            <h3 id="dashboard-momentum">Momentum</h3>
            <p>
              Net providers made bookable since the previous import, split into gained and lost, plus
              a count of items that have been patient-impacting for <strong>30+ days</strong>.
            </p>
            <div className="guide-callout guide-callout-warning">
              <p>
                A rising <strong>&ldquo;Stopped being bookable&rdquo;</strong> number is a regression
                signal - something changed in Epic that took availability away. Chase it before it
                compounds.
              </p>
            </div>

            <h3 id="dashboard-deep">Beyond configuration: what the deep probe found</h3>
            <p>
              Scoped to the providers <strong>patients cannot book</strong>: those whose pairings
              passed every configuration rule and still returned no appointments. The probe splits
              them into a genuine capacity group and an Epic build group that has a fix. See{' '}
              <button type="button" className="guide-hint" onClick={() => jump('levels')}>Level 1 vs Level 2</button> for
              the full breakdown.
            </p>
            <p>
              The footnote counts providers with a deep finding on some pairing who are already
              bookable elsewhere. That is build-team detail, not lost access; their visit-type fixes
              appear per department in <em>Fix once, unblock many</em>.
            </p>

            <h3 id="dashboard-leverage">Fix once, unblock many</h3>
            <p>
              The highest-leverage departments. A provider counts toward a department only when they
              are on the roster and the department is on <strong>their own</strong> published
              Marketing Master locations, so a hospital location that also carries its operating
              rooms does not make an OR an opportunity. Counted as{' '}
              <strong>provider-department fixes, not distinct providers</strong> - a provider blocked
              in two clinics appears in both. Click through to the department queue.
            </p>
          </section>

          <section className="guide-section" id="departments">
            <h2>
              Department Work Queue
              <Link to="/departments" className="guide-hint-block guide-hint">Open the page</Link>
            </h2>
            <p>
              The provider queue asks <em>what is wrong with this provider</em>. This page asks{' '}
              <em>what would one Epic change fix for a whole clinic</em>. Work it first.
            </p>

            <h3 id="departments-columns">Reading a row</h3>
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Column</th>
                  <th>What it tells you</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="guide-wrap">One fix unblocks</td>
                  <td>
                    Providers a single Epic change here would clear. The default sort. It is the{' '}
                    <strong>larger</strong> of the department-flag and visit-type-build counts, not
                    their sum - the two overlap, so adding them would double-count.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Dept flag</td>
                  <td>
                    Roster providers held back by the department Open Scheduling flag (Epic{' '}
                    <code>DEP 53200</code>), counting only those whose own published locations carry
                    this department. The cheapest possible fix: one setting, whole clinic.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Visit-type build</td>
                  <td>
                    Roster providers hitting a Level 2 visit-type build problem here, same scope.
                    Needs a build change per visit type.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Providers</td>
                  <td>Total providers in the department, blocked or not.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">Reach</td>
                  <td>
                    <strong>Published</strong> - a Symplr Marketing Master location publishes it, so
                    a fix is visible to patients. <strong>Not published</strong> - an Epic fix alone
                    surfaces nobody. <strong>Not for these providers</strong> - published on some
                    location, but not on the locations of the providers blocked here, so a fix would
                    not surface them. <strong>Excluded</strong> - never bookable by design.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Work item</td>
                  <td>
                    Current work-queue status (latest note on hover), when it closed and by whom, and
                    any exclusion with its end date.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Owner</td>
                  <td>The person the item is assigned to, and the team.</td>
                </tr>
              </tbody>
            </table>

            <h3 id="departments-controls">Views and filters</h3>
            <ul>
              <li>
                <strong>Open work</strong> (the default tab) - actionable departments (published in
                Symplr, not never-bookable, something to fix) whose item is neither closed nor
                excluded. <strong>All departments</strong> puts operating rooms, cath labs, nuclear
                medicine and echo at the top, with the message-routing department above all of them.
              </li>
              <li>
                <strong>Excluded</strong> and <strong>History</strong> - see{' '}
                <button type="button" className="guide-hint" onClick={() => jump('queue-history')}>History and automatic closure</button>.
              </li>
              <li>
                <strong>Column filters</strong> - the &#9661; in a column header opens an
                Excel-style list of that column&apos;s values with counts. Untick to hide; use
                &ldquo;only&rdquo; to keep a single value. Active filters show as removable chips
                above the table.
              </li>
              <li>
                <strong>Sorting</strong> - click a column header. Defaults to &ldquo;One fix
                unblocks&rdquo;; sort by &ldquo;Dept flag&rdquo; when you want a batch of cheap wins
                to raise in a single ticket.
              </li>
              <li>
                <strong>Search</strong> - department name or Epic department ID.
              </li>
            </ul>

            <h3 id="departments-ticket">Details, notes and tickets</h3>
            <p>Expanding a row gives you what a ticket needs:</p>
            <ul>
              <li>
                <strong>Opportunity</strong> - the one-line statement of what to ask for.
              </li>
              <li>
                <strong>Visit types to name in the ticket</strong> - the specific visit types, how
                many providers each holds back, the Level 2 finding, and <strong>Epic&apos;s own
                message</strong>. Paste Epic&apos;s message into the ticket; it frequently names the
                mismatch directly.
              </li>
              <li>
                <strong>Department work queue</strong> - add a note and set the status, assigned
                team, the person it is assigned to, and any exclusion. Put the ticket number in the
                note.
              </li>
              <li>
                <strong>See the individual providers</strong> - deep-links into the Provider Work
                Queue filtered to that department, with the default filters lifted so the list is not
                empty.
              </li>
            </ul>

            <h3 id="departments-statuses">Work-queue statuses</h3>
            <p>Both queues share one status list, edited in Settings. The defaults are:</p>
            <p>
              <code>New</code> &rarr; <code>Investigating</code> &rarr; <code>Ticket Raised</code>{' '}
              &rarr; <code>Fixed - Awaiting Snapshot</code> &rarr; <code>Resolved</code>, plus{' '}
              <code>Auto-Resolved</code>, which only the rescan sets.
            </p>
            <p>
              Every status has a stage: <strong>Open</strong> and <strong>In progress</strong> are
              open work; <strong>Closed</strong> moves the item to History. Teams, exclusion types and
              the never-bookable department list are edited in Settings too, without a deployment.
            </p>
            <div className="guide-callout">
              <p>
                Use <strong>Fixed - Awaiting Snapshot</strong> when Epic says the change is in but no
                import has confirmed it yet. If the next snapshot confirms it, the rescan closes the
                item as <code>Auto-Resolved</code>; if not, it stays open, which is itself useful.
              </p>
            </div>
          </section>

          <section className="guide-section" id="provider-queue">
            <h2>
              Provider Work Queue
              <Link to="/provider-queue" className="guide-hint-block guide-hint">Open the page</Link>
            </h2>
            <p>
              The provider-level detail page. It shows a snapshot, not a live call, so it can
              disagree with the live slot check on a provider&apos;s detail page if Epic changed
              after the snapshot ran.
            </p>

            <h3 id="queue-defaults">The default view</h3>
            <p>
              The page opens on the <strong>Open work</strong> tab, which shows open work rather than
              everything:
            </p>
            <ul>
              <li>providers who are blocked (no slots) and on the Open Scheduling roster,</li>
              <li>
                whose work item is not closed and not excluded,
              </li>
              <li>not counting blocks on never-bookable departments.</li>
            </ul>
            <p>
              Choosing a tier, a deep finding, a <em>last blocker</em> reason or a department also
              brings in providers who have slots - each of those describes providers usually bookable
              somewhere. Active filters show as chips above the table, with a{' '}
              <strong>Reset to defaults</strong> link. If a count looks wrong, read the chips first.
            </p>

            <h3 id="queue-priority">Where to start</h3>
            <p>
              The {P1} {P2} {P3} buttons in the filter bar are the queue - they set the Priority
              column&apos;s filter. Selecting a tier also shows providers who have slots, because a{' '}
              {P2} provider has slots by definition. See{' '}
              <button type="button" className="guide-hint" onClick={() => jump('priority')}>Priority tiers</button>.
            </p>

            <h3 id="queue-reasons">Tiers and the blocking reason</h3>
            <div className="guide-callout guide-callout-key">
              <p>
                <strong>Tiers decide what to work first</strong> - how badly patients are affected.{' '}
                <strong>The blocking reason decides who fixes it and how</strong> - which Epic setting
                or team owns the fix. Pick a tier, then use the reason to batch the work by owner.
              </p>
            </div>
            <p>
              The <strong>Blocking</strong> column has three modes, chosen inside its filter:
            </p>
            <ul>
              <li>
                <strong>First blocker</strong> (default) - the <em>first</em> rule that blocks a
                provider. Present on every feed. Most of the list reports <em>Provider inactive in
                department</em> here, which is one broad problem hiding everything behind it. When
                Epic&apos;s own first gate is a pairing that passed (for example fully booked), the
                first blocking rule is shown instead, and &ldquo;&ndash;&rdquo; means nothing is
                blocked.
              </li>
              <li>
                <strong>Last blocker</strong> - what would still need fixing after everything
                upstream is fixed. <strong>The clearest view of remaining work</strong>; it changes
                the shape of the list dramatically. Only the weekly workbook records it, so the mode
                is disabled on snapshots without it.
              </li>
              <li>
                <strong>Any carried</strong> - every reason a provider carries, not only the one that
                won the rollup. Choose <em>Carries all ticked</em> to find providers blocked several
                different ways at once, which are usually the messiest cases.
              </li>
            </ul>
            <p>
              <strong>Deep finding</strong> is Level 2, applied only to pairings that passed every
              configuration rule and still returned no slots.
            </p>
            <p>
              Hover any reason, status or finding to get its full definition, the Epic reference
              behind it, and the remediation.
            </p>

            <h3 id="queue-filters">Views and column filters</h3>
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Tab</th>
                  <th>Shows</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="guide-wrap">Open work</td>
                  <td>The default - see above.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">Maintenance / onboarding</td>
                  <td>
                    Providers whose only findings are inactive or missing departments. Mixed active
                    issues stay in Open work.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Publishing</td>
                  <td>
                    Bookable in Epic, but not on the public site: the slots are at departments that
                    are not on the provider&apos;s published Marketing Master locations, or the provider
                    has none. Symplr location work, not Epic work. Each row is marked &ldquo;Not on
                    public site&rdquo;. The Priority filter does not apply here: these providers have
                    Epic slots, so a tier filter would only empty the tab.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Excluded</td>
                  <td>Items with an exclusion in force today.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">History</td>
                  <td>Closed items, newest closure first.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">All providers</td>
                  <td>Everything in the snapshot, with only the column filters applied.</td>
                </tr>
              </tbody>
            </table>
            <p>
              Every column with a &#9661; in its header can be filtered, Excel-style: untick values
              to hide them, or hover a value and click <em>only</em>. The counts beside each value
              are what ticking it would show given every other filter. Useful ones:
            </p>
            <ul>
              <li>
                <strong>Epic status</strong> - Epic&apos;s verdict for the provider&apos;s best
                pairing. <code>FULLY-BOOKED</code> means one pairing passed every rule but is full;
                another pairing can still be blocked, so read <strong>Blocking</strong> before
                dismissing it.
              </li>
              <li>
                <strong>Roster</strong> - Symplr activity and roster membership, for reconciling the
                directory.
              </li>
              <li>
                <strong>Public blocks</strong> - also carries <em>Public site</em>: whether the Epic
                slots can be shown publicly. Choosing a value brings providers with slots into Open
                work.
              </li>
              <li>
                <strong>Work item</strong> - status, exclusion, and <em>Department cover</em>: untick
                &ldquo;Covered by a department item&rdquo; while working the provider list, so you stop
                duplicating work tracked in the department queue.
              </li>
              <li>
                <strong>Owner</strong> - team and assigned person. Tick only yourself for your own
                list.
              </li>
            </ul>
            <p>
              Blocks on never-bookable departments are not counted by default; the note above the
              table says which departments, with a link to count them anyway or to edit the list.
            </p>

            <h3 id="queue-history">History and automatic closure</h3>
            <p>
              Providers never drop out of a queue silently. After every weekly workbook and every
              nightly scan, a rescan reconciles the items someone has opened:
            </p>
            <ul>
              <li>
                An open item whose provider now has slots (or, for a department, nothing left to fix)
                is closed as <code>Auto-Resolved</code> by a system note. It moves to the{' '}
                <strong>History</strong> tab, keeping its owner and notes.
              </li>
              <li>
                <code>Resolved</code> stays the human sign-off: set it when you have verified the fix
                yourself, or confirm an Auto-Resolved item by setting Resolved.
              </li>
              <li>
                An item that closed while the provider was bookable and is now blocked again is
                reopened as <code>New</code>, with a note saying so. A person&apos;s{' '}
                <code>Resolved</code> on a still-blocked provider is left alone.
              </li>
              <li>
                An exclusion with an end date returns the item to Open work on that date, flagged
                &ldquo;Exclusion ended&rdquo;.
              </li>
            </ul>

            <h3 id="queue-tiles">Movement tiles</h3>
            <ul>
              <li>
                <strong>Lost slots / Gained slots / Reason changed</strong> - movement since the
                previous baseline. <strong>Lost slots is the regression watchlist</strong>: someone
                was bookable and now is not.
              </li>
            </ul>

            <h3 id="queue-rows">Working a row</h3>
            <p>
              <strong>Details &amp; Notes</strong> expands the blocked pairings and the note history.
              Record what you found and the ticket number, then set the status, the team, the person
              it is assigned to (searched from the directory, or <em>Me</em>), and any exclusion. An
              exclusion can carry an <em>Excluded until</em> date; types that require one will not
              save without it.
            </p>
            <p>Badges to act on:</p>
            <ul>
              <li>
                <strong>&ldquo;Looks fixed - confirm&rdquo;</strong> - the snapshot shows no block, but
                the newest note still has it open. The next rescan closes it as{' '}
                <code>Auto-Resolved</code>; set <code>Resolved</code> now if you have verified it.
              </li>
              <li>
                <strong>&ldquo;Exclusion ended&rdquo;</strong> - the exclusion&apos;s end date has
                passed and the item is back in open work. Re-exclude it or work it.
              </li>
              <li>
                <strong>&ldquo;Covered by dept item&rdquo;</strong> - an open department item already
                covers the department blocking this provider. Work it there, once, not here.
              </li>
              <li>
                <strong>&ldquo;Dept config&rdquo;</strong> (under a {P3} pill) - the provider is
                held back by the department-level Open Scheduling flag. Click it to open the
                department queue on this provider&apos;s departments: one change there clears every
                provider behind it. These departments are not published for the provider yet, and
                many are operating rooms, so confirm the department is meant to go live before
                asking for the flag.
              </li>
            </ul>
            <p>
              <strong>View profile</strong> opens the live provider page. It is disabled for
              providers inactive in Symplr, because the live directory has no record for them.
            </p>
            <p>
              The profile page carries the same work: its <strong>Work queue</strong> panel shows the
              provider&apos;s item and note history, plus the department items on the departments
              blocking it (or carrying a Level 2 build finding), and lets you add notes to either.
              It opens on its own when there is open work, and the Pre-check line summarizes it. A
              note added from either page shows on both.
            </p>
          </section>

          <section className="guide-section" id="levels">
            <h2>Level 1 vs Level 2 - why &ldquo;fully booked&rdquo; is not always full</h2>
            <p>
              <strong>Level 1</strong> is the configuration cascade. It walks Epic&apos;s rules in
              order and names the first gate that fails: department flag off, provider flag off,
              provider inactive in department, no future template, visit type not schedulable,
              outside the days-out window, blocked or restricted, and so on. Each has a stated Epic
              reference and a remediation, visible on hover in the provider queue.
            </p>
            <p>
              <strong>Level 2</strong> runs only on pairings that passed <em>every</em> Level 1 rule
              and still returned no appointments. Those all used to read as &ldquo;fully
              booked&rdquo;. A deeper search over a wider window separates them.
            </p>

            <h3>Genuine capacity - no Epic fix exists</h3>
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Finding</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="guide-wrap">Available beyond the booking window</td>
                  <td>Capacity / access</td>
                </tr>
                <tr>
                  <td className="guide-wrap">No open time for any visit type</td>
                  <td>Capacity / templates</td>
                </tr>
              </tbody>
            </table>

            <h3>Fixable - a real Epic problem</h3>
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Finding</th>
                  <th>Owner</th>
                  <th>What to ask for</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="guide-wrap">Template has slots, search returns none</td>
                  <td>Epic build team</td>
                  <td>
                    Compare the visit type&apos;s block requirement against the blocks built on the
                    template. Epic&apos;s <code>AS_ErrCode 10</code> message often names the mismatch
                    directly.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Visit type block or length mismatch</td>
                  <td>Epic build team</td>
                  <td>
                    Check the visit type&apos;s block requirement <em>and</em> its length against the
                    open time on the template. The diagnostic cannot yet tell you which of the two it
                    is; expect one or both.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Provider not in the visit type pool</td>
                  <td>Epic build team</td>
                  <td>
                    Add the provider to the pool Epic names in its message, or point the visit type
                    at a pool they are already in.
                  </td>
                </tr>
                <tr>
                  <td className="guide-wrap">Open Scheduling refused the request</td>
                  <td>Epic build team</td>
                  <td>Escalate with the OS error code - nothing downstream was evaluated.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">Other Epic search error</td>
                  <td>Epic build team</td>
                  <td>Escalate with the returned error code.</td>
                </tr>
                <tr>
                  <td className="guide-wrap">Provider unavailable every day scanned</td>
                  <td>Scheduling ops</td>
                  <td>Review the provider&apos;s unavailability entries.</td>
                </tr>
              </tbody>
            </table>

            <h3>Diagnostic quality, not findings</h3>
            <p>
              <strong>Epic search timed out</strong> - treat the pairing as unproven rather than
              blocked, and re-run. <strong>Deep probe errored</strong> - report the run. Seeing zero
              of both is what makes the other numbers trustworthy.
            </p>
          </section>

          <section className="guide-section" id="priority">
            <h2>Priority tiers, stated exactly</h2>
            <p>
              Nothing here is a score. Each tier is a rule over three verifiable facts: whether the
              provider is active in the directory, whether a <em>published</em> department is
              blocked, and whether the provider has any bookable slot at all.
            </p>
            <ul>
              <li>
                {P1} <strong>Live blackout.</strong> Active, blocked on a department patients can
                reach, and no bookable slot anywhere.
              </li>
              <li>
                {P2} <strong>Live gap.</strong> Active, blocked on a department patients can reach,
                bookable elsewhere.
              </li>
              <li>
                {P3} <strong>Not yet reachable.</strong> Active, no bookable slot anywhere, but none
                of the blocked departments are published in Symplr yet.
              </li>
            </ul>
            <p>
              Providers inactive in the directory are never prioritised, and neither are maintenance /
              onboarding records - providers whose only findings are inactive or missing departments.
              They have their own tab in the provider queue and their own count on the Dashboard.
            </p>
            <div className="guide-callout guide-callout-warning">
              <p>
                A blank priority does not mean &ldquo;fine&rdquo;. Providers with only a Level 2
                finding carry no tier, because the configuration cascade found nothing wrong with
                them.
              </p>
            </div>
          </section>

          <section className="guide-section" id="faq">
            <h2>Common questions</h2>

            <h3>The list is all &ldquo;Provider inactive in department&rdquo;.</h3>
            <p>
              That is the <em>first</em> gate. Switch the Blocking column to{' '}
              <button type="button" className="guide-hint" onClick={() => jump('queue-reasons')}>Last blocker</button>{' '}
              to see what is left after it. The population drops sharply.
            </p>

            <h3>A count in a column filter does not match what I get when I tick it.</h3>
            <p>
              Each count is what ticking that value would return, with the column&apos;s own filter
              left out. If they still differ, read the chips above the table - another filter is
              narrowing the list.
            </p>

            <h3>A provider I was working disappeared from Open work.</h3>
            <p>
              Look in <strong>History</strong>. If the rescan found the provider bookable, it closed
              the item as <code>Auto-Resolved</code>; it reopens on its own if the provider is
              blocked again.
            </p>

            <h3>The Dashboard and the provider queue disagree.</h3>
            <p>
              The Dashboard deducts department-covered items from the resolution counts but not from
              patient impact. Impact is what a patient sees; resolution is work outstanding. They are
              meant to differ.
            </p>

            <h3>A provider shows blocked here but bookable on their profile page.</h3>
            <p>
              The queue is a snapshot; the profile does a live check. Epic changed after the snapshot
              ran, and the next import will agree.
            </p>

            <h3>I fixed something and nothing moved.</h3>
            <p>
              Movement is measured between imports. Set the item to{' '}
              <code>Fixed - Awaiting Snapshot</code>; when an import confirms the fix, the rescan
              closes it as <code>Auto-Resolved</code>.
            </p>

            <h3>Why is a department with hundreds of providers not at the top?</h3>
            <p>
              Either it is not published in Symplr, or it is on the excluded list - operating rooms,
              cath labs and similar, which are never open-schedulable by design. Use the{' '}
              <strong>All departments</strong> tab and the <strong>Reach</strong> column to see which;
              the never-bookable list is edited in Settings.
            </p>

            <h3>Nothing loads, or it says there is no Epic diagnostic snapshot.</h3>
            <p>
              No import has been ingested for the selected data environment. Check the environment
              selector in the header first.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default OperationsGuide;
